import { useState } from "react";
import { ShoppingCart, Star, RefreshCw } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useCart, startSubscription } from "@/context/CartContext";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/dibvmg73_kens%20gold.png";

const products = [
  { id: 2, lookupKey: "pack_2oz_7", name: "Ken's Gold 2oz 7-Pack", tagline: "Your weekly wellness ritual.", description: "One shot every morning, seven days a week. By day three, you'll feel the rhythm \u2014 clearer mornings, steadier energy, and a gut that just works. This is where the habit starts.", price: 34.99, subscriptionPrice: 24.99, perShot: "$5.00 / shot — a full week of wellness", size: "2oz", pack: "7-Pack", badge: "Most Popular", badgeClass: "popular", image: "/bottle.webp" },
  { id: 4, lookupKey: "pack_4oz_7", name: "Ken's Gold 4oz 7-Pack", tagline: "The powerhouse bundle for serious wellness.", description: "For the committed. Seven full-strength 4oz shots \u2014 one for every day of the week \u2014 because real transformation happens when you show up every single day.", price: 49.99, subscriptionPrice: 39.99, perShot: "$7.14 / shot — a full week of wellness", size: "4oz", pack: "7-Pack", badge: "Best Value", badgeClass: "value", image: "/bottle-4oz.webp" },
];

const whyItWorks = [
  { id: "ginger", ingredient: "Organic Ginger", benefit: "supports digestion and circulation" },
  { id: "lemon", ingredient: "Organic Lemon", benefit: "delivers natural vitamin C" },
  { id: "honey", ingredient: "Raw Honey", benefit: "provides antioxidants + smooths the burn" },
  { id: "acv", ingredient: "Organic ACV", benefit: "supports gut balance" },
  { id: "greentea", ingredient: "Organic Green Tea", benefit: "adds clean, focused energy" },
];

/* ─── Sub-components ─── */

function ShopHero() {
  return (
    <section className="hero-gradient text-center py-16 md:py-24 px-6" data-testid="shop-hero">
      <img src={LOGO_URL} alt="Ken's Gold" className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 object-contain" />
      <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Ken's Gold Shop</p>
      <h1 className="font-playfair text-4xl md:text-6xl font-bold text-brown-dark italic mb-4" data-testid="shop-title">Choose Your Shot</h1>
      <p className="text-gray-600 max-w-lg mx-auto text-base leading-relaxed mb-2">
        Pick the size that fits your lifestyle — every 7-pack is a full week of shots, crafted with raw honey and organic ingredients. No preservatives, no fillers. Flat $7 shipping · Free over $65 after discounts.
      </p>
      <p className="text-gray-400 text-sm italic">Loved by athletes, founders, and anyone chasing clean daily energy.</p>
      <Link to="/crafting-storage" className="inline-block mt-5 text-sm text-gold underline underline-offset-4 hover:text-brown-dark transition-colors" data-testid="shop-storage-link">How it’s crafted &amp; how to keep it fresh</Link>
    </section>
  );
}

function SubscribeToggle({ isSub, setIsSub, id }) {
  return (
    <div className="flex rounded-lg border border-gold/25 p-1 bg-cream-light mb-4 text-xs font-semibold" data-testid={`purchase-toggle-${id}`}>
      <button onClick={() => setIsSub(false)} className={`flex-1 py-1.5 rounded-md transition-colors ${!isSub ? "bg-white text-brown-dark shadow-sm" : "text-gray-400"}`} data-testid={`one-time-${id}`}>One-time</button>
      <button onClick={() => setIsSub(true)} className={`flex-1 py-1.5 rounded-md transition-colors ${isSub ? "bg-gold text-white shadow-sm" : "text-gray-400"}`} data-testid={`subscribe-${id}`}>Subscribe &amp; Save</button>
    </div>
  );
}

function SubscribeComparison({ product }) {
  const savings = product.price - product.subscriptionPrice;
  return (
    <div className="grid grid-cols-2 border-y border-gold/15 mb-4" data-testid={`subscription-comparison-${product.id}`}>
      <div className="py-3 pr-3 border-r border-gold/15 min-w-0" data-testid={`one-time-comparison-${product.id}`}>
        <p className="text-[11px] text-gray-500 mb-1">One-time</p>
        <p className="font-playfair text-lg font-bold text-brown-dark" data-testid={`one-time-comparison-price-${product.id}`}>${product.price.toFixed(2)}</p>
      </div>
      <div className="py-3 pl-3 min-w-0" data-testid={`subscribe-comparison-${product.id}`}>
        <p className="text-[11px] font-semibold text-[#326047] mb-1">Subscribe &amp; Save</p>
        <p className="font-playfair text-lg font-bold text-[#326047]" data-testid={`subscribe-comparison-price-${product.id}`}>${product.subscriptionPrice.toFixed(2)}<span className="font-dm text-[11px] font-medium text-gray-500"> / week</span></p>
        <p className="text-[10px] text-[#326047] mt-1" data-testid={`subscribe-comparison-savings-${product.id}`}>Save ${savings.toFixed(2)} every week</p>
      </div>
    </div>
  );
}

function PriceBlock({ product, isSub }) {
  const price = isSub ? product.subscriptionPrice : product.price;
  const perShot = isSub ? `$${(price / 7).toFixed(2)} / shot — subscription price` : product.perShot;
  return (
    <div>
      {isSub && <span className="block text-xs text-gray-400 line-through">${product.price}</span>}
      <span className="font-playfair text-3xl font-bold text-gold" data-testid={`price-${product.id}`}>${Number.isInteger(price) ? price : price.toFixed(2)}</span>
      {perShot && <span className="block text-xs text-gold/70 mt-1" data-testid={`per-shot-${product.id}`}>{perShot}</span>}
      {isSub && (
        <span className="flex items-center gap-1 text-[11px] text-gray-400 mt-1" data-testid={`sub-note-${product.id}`}>
          <RefreshCw size={10} /> Billed weekly via PayPal · $7 shipping first order, then free · Cancel anytime
        </span>
      )}
    </div>
  );
}

function ProductCard({ product }) {
  const [isSub, setIsSub] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { addItem } = useCart();

  const handleAction = async () => {
    if (!isSub) {
      addItem({ lookupKey: product.lookupKey, name: product.name, price: product.price, image: product.image });
      return;
    }
    setLoading(true);
    setError("");
    try {
      await startSubscription(product.lookupKey);
    } catch (e) {
      setError(e.message || "Could not start subscription.");
      setLoading(false);
    }
  };

  return (
    <div className="product-card relative flex flex-col" data-testid={`product-${product.id}`}>
      {product.badge && (
        <span className={`product-badge ${product.badgeClass}`} data-testid={`badge-${product.id}`}>{product.badge}</span>
      )}
      <div className="px-6 pt-6 pb-2 flex justify-center bg-cream">
        <img src={product.image} alt={product.name} className="h-56 w-auto object-contain drop-shadow-lg" data-testid={`product-image-${product.id}`} />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex gap-2 mb-2">
          <span className="size-badge bg-honey-tint text-gold">{product.size}</span>
          <span className="size-badge bg-gray-100 text-gray-600">{product.pack}</span>
        </div>
        <h3 className="font-playfair text-lg font-bold text-brown-dark mb-1" data-testid={`product-name-${product.id}`}>{product.name}</h3>
        <p className="text-gold text-xs font-semibold italic mb-3" data-testid={`product-tagline-${product.id}`}>{product.tagline}</p>
        <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{product.description}</p>
        <SubscribeComparison product={product} />
        <SubscribeToggle isSub={isSub} setIsSub={setIsSub} id={product.id} />
        {error && <p className="text-red-400 text-xs mb-2" data-testid={`sub-error-${product.id}`}>{error}</p>}
        <div className="flex flex-wrap items-end justify-between mt-auto gap-3">
          <PriceBlock product={product} isSub={isSub} />
          <button onClick={handleAction} disabled={loading} className="btn-gold text-sm py-2.5 px-5 flex-shrink-0 disabled:opacity-60" data-testid={`add-to-cart-${product.id}`}>
            <ShoppingCart size={16} /> {loading ? "Redirecting..." : isSub ? "Subscribe" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsGrid() {
  return (
    <section className="py-16 px-6" data-testid="products-section">
      <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-8">
        {products.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

function WhyItWorksSection() {
  return (
    <section className="py-16 px-6 bg-cream" data-testid="why-it-works-section">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Simple Science</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-10">Why It Works</h2>
        <div className="space-y-3 text-left max-w-md mx-auto">
          {whyItWorks.map((item) => (
            <div key={item.ingredient} className="flex items-center gap-3 bg-white border border-gold/10 rounded-xl px-5 py-3.5" data-testid={`why-${item.id}`}>
              <Star size={14} className="text-gold flex-shrink-0" />
              <span className="text-sm">
                <strong className="text-brown-dark">{item.ingredient}</strong>{" "}
                <span className="text-gray-500">{item.benefit}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IngredientsBanner() {
  return (
    <section className="cta-gradient py-16 px-6 text-center" data-testid="ingredients-banner">
      <p className="text-gold-light text-sm mb-4">Every shot contains</p>
      <h2 className="font-playfair text-2xl md:text-4xl font-bold text-white mb-4">
        Organic Ginger &middot; Organic Lemon &middot; Raw Honey &middot; Organic Apple Cider Vinegar &middot; Organic Green Tea
      </h2>
      <p className="text-white/60 text-sm mb-8">100% Raw &middot; No preservatives &middot; No artificial flavors</p>
      <p className="text-white/90 font-playfair text-xl md:text-2xl italic" data-testid="shop-final-cta">
        Start your wellness ritual today — your body will thank you.
      </p>
    </section>
  );
}

/* ─── Main Component ─── */

export default function Shop() {
  useSEO({
    title: "Shop Wellness Shots",
    description: "Shop Ken's Gold wellness shots. 2oz and 4oz weekly 7-packs from $34.99 — as low as $5.00 per shot. 100% raw, organic ingredients. Free from preservatives and artificial flavors. Subscribe & save.",
  });

  return (
    <div data-testid="shop-page">
      <ShopHero />
      <ProductsGrid />
      <WhyItWorksSection />
      <IngredientsBanner />
    </div>
  );
}
